import { Divider, Grid, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { bbfcRatingOptions, europeanFskRatingOptions, mpaaRatingOptions } from '@/components/SeriesContainer/SeriesConstants';
import VNAutocomplete from '@/components/VNAutocomplete/VNAutocomplete';
import VNSection from '@/components/VNSection/VNSection';
import VNSelect from '@/components/VNSelect/VNSelect';
import VNTextField from '@/components/VNTextField/VNTextField';
import { Permission } from '@/enums';
import AssetVariantList from '@/pages/assets/asset-fields-widget/AssetVariantList';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';

interface Props {
  isMultiple?: boolean;
  isNew?: boolean;
}
const Metadata = ({ isMultiple, isNew }: Props) => {
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const isSuperAdmin = useMemo(() => permissions && permissions.has(Permission.OUSuperAdmin), [permissions]);

  const assetForm = useFormContext();
  const { watch } = assetForm;
  const schemaType = watch('schemaType');
  const sourceWatch = watch('source');

  let cachedSectionStates;
  if (isNew) {
    const prevState = sessionStorage.getItem('accordianStates') || '{"accordian": [false, false, false, false]}';
    cachedSectionStates = JSON.parse(prevState).accordian;
  } else {
    cachedSectionStates = [true, true, true, true];
  }

  const [sectionStates, setSectionStates] = useState(cachedSectionStates);

  return (
    <div>
      <VNSection
        name="Program"
        label="Program Tags"
        variant="dropdown"
        isOpen={sectionStates[0]}
        toggleCallback={() => toggleCallback(0)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNAutocomplete
            name="common.series"
            label="Series Name"
            multiple
            hideOptions
            options={watch('common.series')?.map((series: string) => ({ label: series, value: series }))}
          />
          <VNAutocomplete
            name="common.season"
            label="Season Number"
            multiple
            hideOptions
            options={watch('common.season')?.map((season: string) => ({ label: season, value: season }))}
          />
          <VNTextField name="common.episode" label="Episode Number" disabled={isMultiple} />
          <VNAutocomplete
            name="common.cast"
            label="Cast"
            multiple
            hideOptions
            options={watch('common.cast')?.map((cast: string) => ({ label: cast, value: cast }))}
          />
          <VNAutocomplete
            name="common.producer"
            label="Producers"
            multiple
            hideOptions
            options={watch('common.producer')?.map((producer: string) => ({ label: producer, value: producer }))}
          />
          <VNAutocomplete
            name="common.director"
            label="Director(s)"
            multiple
            hideOptions
            options={watch('common.director')?.map((director: string) => ({ label: director, value: director }))}
          />
          <VNAutocomplete
            name="common.genre"
            label="Genres"
            multiple
            hideOptions
            options={watch('common.genre')?.map((genre: string) => ({ label: genre, value: genre }))}
          />
          <VNTextField name="common.programType" label="Program Type" />
          <VNSelect name="common.mpaaRating" label="MPAA Rating" options={mpaaRatingOptions} />
          <VNSelect name="common.bbfcRating" label="BBFC Rating" options={bbfcRatingOptions} />
          <VNSelect name="common.usParentalRatings" label="US Parental Ratings" options={bbfcRatingOptions} />
          <VNSelect name="common.europeanFskRating" label="European FSK Rating" options={europeanFskRatingOptions} />
        </div>
      </VNSection>

      <VNSection
        name="Sports"
        label="Sports Tags"
        variant="dropdown"
        isOpen={sectionStates[1]}
        toggleCallback={() => toggleCallback(1)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNAutocomplete
            name="common.teamName"
            label="Team Names"
            multiple
            hideOptions
            options={watch('common.teamName')?.map((team: string) => ({ label: team, value: team }))}
          />
          <VNAutocomplete
            name="common.leagueName"
            label="League names"
            multiple
            hideOptions
            options={watch('common.leagueName')?.map((league: string) => ({ label: league, value: league }))}
          />
        </div>
      </VNSection>

      <VNSection
        name="Network"
        label="Network Tags"
        variant="dropdown"
        isOpen={sectionStates[2]}
        toggleCallback={() => toggleCallback(2)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTextField name="common.networkValue" label="Network Value" />
          <VNTextField name="common.tmsidEpisode" label="TMSID Episode" />
          <VNTextField name="common.tmsidSeries" label="TMSID Series" />
        </div>
      </VNSection>
      <VNSection
        name="Advanced"
        label="Advanced"
        variant="dropdown"
        isOpen={sectionStates[3]}
        toggleCallback={() => toggleCallback(3)}
      >
        <div className="form-grid" style={{ marginBottom: '1em' }}>
          <VNTextField name="markers" disabled label="Midroll Ad Cue Points" />
          {schemaType === 'Video' && <VNTextField name="subTitle" label="Sub-title" />}
        </div>
        {isSuperAdmin && !isNew && schemaType === 'Video' && sourceWatch !== 'NestedChannel' && sourceWatch !== 'ClipGroup' && (
          <Grid item xs={12} className="variants-container">
            <Divider />
            <Typography component="div" sx={{ marginTop: 2, marginBottom: 1 }}>
              Variants
            </Typography>
            <AssetVariantList assetFormValues={assetForm} />
          </Grid>
        )}
      </VNSection>
    </div>
  );
  // function useSectionHasValues(names: string[]) {
  //   let result = false;
  //   names.forEach((name) => {
  //     const value = getValues(name);
  //     if (value !== null && value !== undefined && value !== '') {
  //       result = true;
  //     }
  //   });
  //   return result;
  //   // const values = useWatch({ name: names });
  //   // return values.some((value) => {
  //   //   if (Array.isArray(value)) return value.length > 0;
  //   //   return value !== null && value !== undefined && value !== '';
  //   // });
  // }
  function toggleCallback(index: number) {
    const newStates = [...sectionStates];
    newStates[index] = !newStates[index];
    setSectionStates(newStates);
    if (isNew) {
      sessionStorage.setItem('accordianStates', JSON.stringify({ accordian: newStates }));
    }
  }
};

export default Metadata;
